import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PRIORITY_STYLES } from "./helpers";

// Priority chip with optional tap-to-edit popover (tasks only).
export default function PriorityChip({ testId, priority, completed, onEditPriority }) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const wrapperRef = useRef(null);

    if (!priority) return null;

    if (!onEditPriority || completed) {
        return (
            <span className={`text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full ${PRIORITY_STYLES[priority]}`}>
                {priority}
            </span>
        );
    }

    return (
        <span ref={wrapperRef} className="relative inline-flex">
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setPickerOpen((v) => !v);
                }}
                className={`text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full transition-all hover:brightness-95 active:scale-95 ${PRIORITY_STYLES[priority]}`}
                data-testid={`${testId}-priority-chip`}
                aria-haspopup="listbox"
                aria-expanded={pickerOpen}
                aria-label={`Priority ${priority}. Tap to change.`}
                title="Tap to change priority"
            >
                {priority}
            </button>
            <AnimatePresence>
                {pickerOpen && (
                    <>
                        <div
                            className="fixed inset-0 z-30"
                            onClick={() => setPickerOpen(false)}
                        />
                        <motion.div
                            key="priority-picker"
                            initial={{ opacity: 0, y: -4, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -4, scale: 0.95 }}
                            transition={{ duration: 0.15 }}
                            className="absolute left-0 top-full mt-1 z-40 bg-cream rounded-2xl shadow-float border border-warm-300/40 py-2 px-2 w-44"
                            data-testid={`${testId}-priority-picker`}
                            onClick={(e) => e.stopPropagation()}
                            role="listbox"
                        >
                            <p className="font-display text-[10px] uppercase tracking-widest text-warm-500 px-1 pb-1.5">
                                Priority
                            </p>
                            <div className="flex flex-col gap-1">
                                {["low", "medium", "high"].map((p) => {
                                    const active = priority === p;
                                    return (
                                        <button
                                            key={p}
                                            onClick={async () => {
                                                setPickerOpen(false);
                                                if (p === priority) return;
                                                try { await onEditPriority(p); } catch (e) { console.warn("priority edit failed (parent shows toast)", e); }
                                            }}
                                            className={`flex items-center justify-between font-display text-xs px-2.5 py-1.5 rounded-xl transition-all active:scale-95 ${
                                                active
                                                    ? `${PRIORITY_STYLES[p]} ring-2 ring-terracotta/30`
                                                    : "hover:bg-surface text-warm-900"
                                            }`}
                                            data-testid={`${testId}-priority-${p}`}
                                            role="option"
                                            aria-selected={active}
                                        >
                                            <span className="uppercase tracking-wider">{p}</span>
                                            <span
                                                aria-hidden="true"
                                                className={`inline-block w-2 h-2 rounded-full ${
                                                    p === "low" ? "bg-sage" :
                                                    p === "medium" ? "bg-mustard" :
                                                    "bg-terracotta"
                                                }`}
                                            />
                                        </button>
                                    );
                                })}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </span>
    );
}
